"use client";

import { ChevronDown, ChevronRight, X } from "lucide-react";
import Image from "next/image";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type PaymentMethodControl = {
  title?: string;
  label: string;
  badge?: string | null;
  /**
   * The method's own mark, when it has one — a UPI app logo today.
   *
   * Takes the badge's place rather than sitting beside it: "PhonePe" under a
   * generic `UPI` chip makes the payer read to find out what they picked, while
   * the logo is recognised before the text is. The badge stays the fallback for
   * everything without a mark (cards, a typed UPI ID).
   */
  iconUrl?: string | null;
  subtitle?: string | null;
  disabled?: boolean;
  onOpenSelector?: () => void;
};

type PaymentInlineMessage = {
  tone?: "neutral" | "warning" | "error" | "expired";
  message: string;
  /**
   * The message's own call to action.
   *
   * Copy like "retry below" or "it will confirm shortly" was pointing at a
   * button somewhere else on the screen, or at nothing at all. If a message asks
   * for something, it carries the control that does it.
   */
  action?: {
    label: string;
    onClick: () => void;
    pending?: boolean;
    pendingLabel?: string;
  };
};

type PaymentShellProps = {
  children: ReactNode;
  totalDisplay: string;
  totalLabel?: string;
  footerMessage?: string | null;
  paymentMethod?: PaymentMethodControl;
  inlineMessage?: PaymentInlineMessage | null;
  ctaLabel: string;
  ctaLoadingLabel?: string;
  ctaLoading?: boolean;
  ctaDisabled?: boolean;
  onCtaClick?: () => void;
  onDismissInlineMessage?: () => void;
  /**
   * Rendered inside a brand scope (lib/branding): take the page, footer and
   * pay button from the scoped tokens instead of the app's fixed dark-mode
   * colours, so a partner's checkout stays in their colours to the end.
   */
  branded?: boolean;
};

export function PaymentShell({
  children,
  totalDisplay,
  totalLabel = "Total Payable",
  footerMessage,
  paymentMethod,
  inlineMessage,
  ctaLabel,
  ctaLoadingLabel = "Opening…",
  ctaLoading = false,
  ctaDisabled = false,
  onCtaClick,
  onDismissInlineMessage,
  branded = false,
}: PaymentShellProps) {
  const inlineMessageClassName =
    inlineMessage?.tone === "expired"
      ? "border-error-border bg-error-bg text-error shadow-lg shadow-red-950/10 dark:border-red-400/40 dark:bg-[#3b1014] dark:text-red-50 dark:shadow-red-950/30"
      : inlineMessage?.tone === "error"
        ? "border-error-border bg-error-bg text-error shadow-lg shadow-black/10 dark:border-rose-700 dark:bg-[#3b101d] dark:text-rose-50 dark:shadow-black/30"
        : inlineMessage?.tone === "warning"
          ? "border-warning-border bg-warning-bg text-warning shadow-lg shadow-black/10 dark:border-amber-700 dark:bg-[#382709] dark:text-amber-50 dark:shadow-black/30"
          : "border-border bg-surface text-text-primary shadow-lg shadow-black/10 dark:border-zinc-700 dark:bg-[#1b1b20] dark:text-white dark:shadow-black/30";

  return (
    <div
      className={cn(
        "payment-shell min-h-dvh bg-background pb-32 text-text-primary",
        !branded && "dark:bg-[#0b0b0f] dark:text-white",
      )}
    >
      <div className="payment-shell-content mx-auto max-w-[520px] space-y-5 px-4 pt-[calc(env(safe-area-inset-top)+12px)]">
        {children}
      </div>

      <div className="safe-area-bottom fixed inset-x-0 bottom-0 z-20">
        <div className="mx-auto max-w-[520px] px-0 pb-0">
          {inlineMessage ? (
            <div
              aria-live="polite"
              className={cn(
                "mx-4 mb-2 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2 rounded-2xl border px-3 py-2.5 text-sm",
                inlineMessage.tone === "expired"
                  ? "text-sm leading-snug font-medium"
                  : "",
                inlineMessageClassName,
              )}
            >
              <div className="min-w-0 pr-1">
                {inlineMessage.message}
                {inlineMessage.action ? (
                  <button
                    type="button"
                    disabled={inlineMessage.action.pending}
                    className="mt-2 flex min-h-9 touch-manipulation items-center justify-center rounded-xl border border-current/25 px-3 py-1.5 text-xs font-semibold text-current transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-current/35 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={inlineMessage.action.onClick}
                  >
                    {inlineMessage.action.pending
                      ? (inlineMessage.action.pendingLabel ?? "Checking…")
                      : inlineMessage.action.label}
                  </button>
                ) : null}
              </div>
              {onDismissInlineMessage ? (
                <button
                  type="button"
                  aria-label="Dismiss message"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-current opacity-80 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-current/35 focus-visible:outline-none"
                  onClick={onDismissInlineMessage}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          ) : null}

          <div
            className={cn(
              "min-h-[88px] border-t border-border px-3.5 pt-2.5 pb-[calc(env(safe-area-inset-bottom)+12px)] shadow-[0_-18px_42px_rgba(15,23,42,0.16)] backdrop-blur-lg sm:min-h-[92px] sm:px-4",
              branded
                ? "bg-background/96"
                : "bg-surface/96 dark:border-white/[0.1] dark:bg-[#121116] dark:shadow-2xl dark:shadow-black/40",
            )}
          >
            {paymentMethod ? (
              <div className="grid grid-cols-[minmax(0,0.36fr)_minmax(0,0.64fr)] items-center gap-2 sm:gap-3">
                <button
                  type="button"
                  className={cn(
                    "min-w-0 touch-manipulation rounded-[18px] px-0 py-0.5 text-left transition-colors hover:bg-surface-alt focus-visible:ring-2 focus-visible:ring-brand/30 focus-visible:outline-none dark:hover:bg-glass-bg-04 dark:focus-visible:ring-white/30",
                    paymentMethod.disabled || ctaLoading
                      ? "cursor-not-allowed opacity-55"
                      : "",
                  )}
                  disabled={
                    paymentMethod.disabled ||
                    ctaLoading ||
                    !paymentMethod.onOpenSelector
                  }
                  onClick={paymentMethod.onOpenSelector}
                >
                  <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
                    {paymentMethod.iconUrl ? (
                      // White plate: these marks are drawn for light grounds,
                      // and several are near-black on transparent.
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-[5px] bg-white">
                        <Image
                          src={paymentMethod.iconUrl}
                          alt=""
                          width={16}
                          height={16}
                          className="h-4 w-4 object-contain"
                          unoptimized
                          loading="eager"
                        />
                      </span>
                    ) : paymentMethod.badge ? (
                      <span className="flex h-5 min-w-7 shrink-0 items-center justify-center rounded-[5px] bg-surface-alt px-1 text-[9px] font-bold tracking-[-0.02em] text-brand-dark uppercase dark:bg-white dark:text-[#1f3b8f]">
                        {paymentMethod.badge}
                      </span>
                    ) : null}
                    <span className="min-w-0 truncate text-xs font-medium whitespace-nowrap text-text-tertiary dark:text-white/42">
                      {paymentMethod.title ?? "Pay using"}
                    </span>
                    <ChevronDown
                      className="h-3.5 w-3.5 shrink-0 text-text-secondary dark:text-white/80"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-1 truncate text-xs leading-tight font-semibold text-text-primary sm:text-sm dark:text-white">
                    {paymentMethod.label}
                  </div>
                  {paymentMethod.subtitle ? (
                    <div className="mt-0.5 truncate text-[10px] font-medium text-text-tertiary sm:text-[11px] dark:text-white/28">
                      {paymentMethod.subtitle}
                    </div>
                  ) : null}
                </button>

                <button
                  type="button"
                  className={cn(
                    "flex min-h-[56px] min-w-0 touch-manipulation items-center justify-between gap-2 rounded-[18px] bg-brand px-3 py-2 text-left text-brand-foreground transition-colors hover:bg-brand-hover focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:outline-none sm:min-h-[62px] sm:rounded-[20px] sm:px-4",
                    !branded &&
                      "dark:bg-white dark:text-black dark:hover:bg-white/95 dark:focus-visible:ring-white/40",
                    ctaDisabled || ctaLoading
                      ? "cursor-not-allowed opacity-60"
                      : "",
                  )}
                  disabled={ctaDisabled || ctaLoading}
                  onClick={onCtaClick}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] leading-none font-bold tracking-[-0.02em] whitespace-nowrap sm:text-base md:text-[17px]">
                      {totalDisplay}
                    </div>
                    <div
                      className={cn(
                        "mt-1 text-[10px] font-semibold tracking-wide whitespace-nowrap text-brand-foreground/80 sm:text-xs",
                        !branded && "dark:text-black/70",
                      )}
                    >
                      {totalLabel}
                    </div>
                    {footerMessage ? (
                      <div
                        className={cn(
                          "mt-0.5 truncate text-[9px] text-brand-foreground/66 sm:text-[10px]",
                          !branded && "dark:text-black/60",
                        )}
                      >
                        {footerMessage}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5 text-[15px] leading-none font-bold tracking-[-0.02em] whitespace-nowrap sm:text-base md:text-[17px]">
                    <span>{ctaLoading ? ctaLoadingLabel : ctaLabel}</span>
                    {!ctaLoading ? (
                      <ChevronRight
                        className="h-4 w-4 shrink-0"
                        aria-hidden="true"
                      />
                    ) : null}
                  </div>
                </button>
              </div>
            ) : (
              <div className="flex w-full min-w-0 items-center gap-3">
                <div className="min-w-0 shrink-0 pr-2">
                  <div className="text-[18px] leading-none font-bold tracking-[-0.02em] whitespace-nowrap text-text-primary sm:text-[19px] md:text-xl dark:text-white">
                    {totalDisplay}
                  </div>
                  <div className="mt-1 text-[11px] font-semibold tracking-wide whitespace-nowrap text-text-tertiary sm:text-xs dark:text-white/50">
                    {totalLabel}
                  </div>
                  {footerMessage ? (
                    <div className="mt-0.5 truncate text-[9px] text-text-tertiary sm:text-[10px] dark:text-white/40">
                      {footerMessage}
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  className={cn(
                    "flex min-h-[56px] min-w-0 flex-1 touch-manipulation items-center justify-between gap-2.5 rounded-[20px] bg-brand px-3.5 py-2.5 font-bold text-brand-foreground shadow-[0_2px_10px_rgba(0,0,0,0.08)] transition-colors hover:bg-brand-hover focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:outline-none sm:min-h-[62px] sm:px-4 dark:bg-white dark:text-black dark:shadow-none dark:hover:bg-white/95",
                    ctaDisabled || ctaLoading
                      ? "cursor-not-allowed opacity-60"
                      : "",
                  )}
                  disabled={ctaDisabled || ctaLoading}
                  onClick={onCtaClick}
                >
                  <span className="min-w-0 flex-1 text-left text-[14px] leading-tight font-bold tracking-[-0.02em] sm:text-[15px] md:text-[16px]">
                    {ctaLoading ? ctaLoadingLabel : ctaLabel}
                  </span>
                  {!ctaLoading ? (
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 dark:bg-black/10">
                      <ChevronRight
                        className="h-4 w-4 shrink-0"
                        aria-hidden="true"
                      />
                    </span>
                  ) : null}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
